import React from "react";
import { Views } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import Calendar from "./index";
import {
  getParticipantsInfoInitials,
  getParticipantsInfo,
  isItCountSlot,
} from "../../Shared/Utils";
import appointmentsService from "../../services/appointmentsService";
import PatientAppServices from "../../services/PatientAppServices";
import { connect } from "react-redux";
import { isPatient, isStaff } from "../../security/roles";
import { devLog } from "../../utils/devLog";
import I18nProvider from "../../services/I18nProvider";
import MPSBarLoader from "../MPSBarLoader";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Text from "../Text";
import Messages from "../../Shared/Messages";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
// Helper function to format date range labels for compact mode with localization
const formatCompactDateRange = (
  label,
  view,
  date,
  isCompact,
  isPatientContext,
  agendaLength,
) => {
  // Always use compact formatting for patient detail views, regardless of screen size
  // Exception: always format agenda view regardless of compact mode or context
  if (
    ((!isCompact && !isPatientContext) || !label || view === "day") &&
    view !== "agenda"
  ) {
    return label;
  }

  // Get current locale from I18nProvider
  const locale = I18nProvider.getLocale();

  // For week views, create a compact date range
  if (view === "week" || view === "work_week") {
    const startOfWeek = moment(date).startOf("week");
    const endOfWeek = moment(date).endOf("week");

    // Use native toLocaleDateString for automatic locale formatting
    const startDate = startOfWeek.toDate();
    const endDate = endOfWeek.toDate();

    const startOptions = { month: "numeric", day: "numeric", year: "2-digit" };
    const startFormatted = startDate.toLocaleDateString(
      undefined,
      startOptions,
    );

    const endOptions = { month: "numeric", day: "numeric", year: "2-digit" };
    const endFormatted = endDate.toLocaleDateString(undefined, endOptions);

    return `${startFormatted} - ${endFormatted}`;
  }

  // For agenda view, format based on locale using the calculated agenda length
  if (view === "agenda") {
    // For agenda view, ignore the provided label and calculate our own range
    // The provided label from react-big-calendar is incorrect

    // We need to calculate the actual start and end based on what was used for the API call
    // The API call uses proper month/week/day boundaries, so let's reconstruct that
    let agendaStart, agendaEnd;

    if (agendaLength === 31 || agendaLength === 30) {
      // This indicates we're showing a full month
      agendaStart = moment(date).startOf("month");
      agendaEnd = moment(date).endOf("month");
    } else if (agendaLength === 7) {
      // This indicates we're showing a week
      agendaStart = moment(date).startOf("week");
      agendaEnd = moment(date).endOf("week");
    } else if (agendaLength === 1) {
      // This indicates we're showing a day
      agendaStart = moment(date);
      agendaEnd = moment(date);
    } else {
      // Fallback to the provided length
      agendaStart = moment(date);
      agendaEnd = moment(date).add((agendaLength || 30) - 1, "days");
    }

    // Use simple abbreviated month format for all locales: "Oct 26 - Nov 01"
    return `${agendaStart.format("MMM DD")} - ${agendaEnd.format("MMM DD")}`;
  }

  // For month view, shorten the month name
  if (view === "month") {
    // Use abbreviated month with 2-digit year for compact mode: "Sep '24"
    return label?.replace(
      /(\w{3})\w*\s(\d{4})/,
      (match, month, year) => `${month} '${year.slice(-2)}`,
    );
  }

  return label;
};

// Dynamic styles function for calendar enhancements
const getCalendarStyles = (themeColor = "#20a8d8", isCompact = false) => `
  .rbc-day-slot .rbc-events-container {
    margin-right: 0px !important;
  }
  /* Time view enhancements */
  .rbc-time-view .rbc-time-gutter .rbc-timeslot-group {
    min-height: 72px !important;  
    border-bottom: none !important;
  }
  
  .rbc-time-view .rbc-day-slot .rbc-timeslot-group {
    min-height: 72px !important;
    border-bottom: none !important;
  }
  
  .rbc-time-view .rbc-time-slot {
    height: 36px !important; /* Half of timeslot-group height (72px / 2) */
    min-height: 36px !important;
    max-height: 36px !important;
    border-bottom: 1px solid #ddd;
    box-sizing: border-box;
  }
  /* Time gutter specific slots */
  .rbc-time-view .rbc-time-gutter .rbc-time-slot {
    height: 36px !important;
    min-height: 36px !important;
    max-height: 36px !important;
    border-bottom: 1px solid #ddd;
    box-sizing: border-box;
}
  
  /* Month view cell styling */
  .rbc-day-bg {
    background-color: #ffffff !important;
  }
  
  /* Off-range (prev/next month) cells with darker gray */
  .rbc-off-range-bg {
    background-color: #e8e9ea !important;
  }
  
  /* Today cell with pale yellow highlight */
  .rbc-today {
    background-color: #fff3cd !important;
  }
  /* Ensure the time labels align properly */
  .rbc-time-view .rbc-time-gutter .rbc-timeslot-group .rbc-time-slot:first-child {
    border-bottom: 1px solid #ccc; /* Slightly darker for hour marks */
  }

  /* Agenda view improvements */
  .rbc-agenda-view {
    border: 1px solid #dee2e6;
    border-radius: 6px;
    overflow: hidden;
    background: white;
  }

  .rbc-agenda-view table {
    margin-bottom: 0;
    border-collapse: separate;
    border-spacing: 0;
  }

  .rbc-agenda-view .rbc-agenda-table {
    width: 100%;
    border: none;
  }

  .rbc-agenda-view .rbc-agenda-table thead th {
    background-color: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
    padding: 12px 16px;
    font-weight: 600;
    color: #495057;
    text-align: left;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .rbc-agenda-view .rbc-agenda-table tbody tr {
    border-bottom: 1px solid #f1f3f4;
    transition: background-color 0.15s ease;
  }

  .rbc-agenda-view .rbc-agenda-table tbody tr:hover {
    background-color: #f8f9fa;
  }

  .rbc-agenda-view .rbc-agenda-table tbody tr:last-child {
    border-bottom: none;
  }

  .rbc-agenda-view .rbc-agenda-table tbody td {
    padding: 12px 16px;
    vertical-align: middle;
    border: none;
    font-size: 0.9rem;
  }

  .rbc-agenda-view .rbc-agenda-table .rbc-agenda-date-cell {
    width: 120px;
    min-width: 120px;
  }

  .rbc-agenda-view .rbc-agenda-table .rbc-agenda-time-cell {
    width: 140px;
    min-width: 140px;
  }

  .rbc-agenda-view .rbc-agenda-table .rbc-agenda-event-cell {
    width: auto;
  }

  .rbc-time-view .rbc-time-gutter .rbc-timeslot-group .rbc-time-slot:last-child {
    border-bottom: 1px solid #eee; /* Lighter for half-hour marks */
  }

  /* Make sure day slots match exactly */
  .rbc-time-view .rbc-day-slot .rbc-timeslot-group .rbc-time-slot {
    height: 36px !important;
    min-height: 36px !important;
    max-height: 36px !important;
    border-bottom: 1px solid #ddd;
    box-sizing: border-box;
  }
    
  /* Enhanced toolbar styling with much darker background and better contrast */
  .rbc-toolbar {
    background: ${
      isCompact
        ? "rgba(173, 216, 230, 0.15)"
        : `linear-gradient(135deg, #e3f2fd, #e3f2fd)`
    };
    backdrop-filter: blur(${isCompact ? "5px" : "15px"});
    border-radius: ${isCompact ? "8px" : "16px"};
    padding: ${isCompact ? "4px 12px" : "8px 20px"} !important;
    margin-bottom: ${isCompact ? "3px" : "6px"} !important;
    margin-top: 2px !important;
    border: ${"1px solid #dee2e6"};
    box-shadow: ${"0 1px 4px rgba(0, 0, 0, 0.1)"};
    min-height: ${isCompact ? "36px" : "48px"} !important;
    display: flex !important;
    align-items: center !important;
  }
  
  .rbc-toolbar-label {
    font-size: ${isCompact ? "14px" : "20px"} !important;
    font-weight: ${isCompact ? "500" : "700"} !important;
    color: ${isCompact ? "#495057" : "#031372ff"} !important;
    text-shadow: ${isCompact ? "none" : "0 1px 1px rgba(0, 0, 0, 0.1)"};
    flex: 1 !important;
    text-align: center !important;
  }
  
  /* Enhanced button styling */
  .rbc-btn-group {
    display: flex !important;
    align-items: center !important;
  }
  
  .rbc-btn-group button {
    padding: ${isCompact ? "2px 8px" : "4px 12px"} !important;
    margin: 0 ${isCompact ? "2px" : "3px"} !important;
    font-size: ${isCompact ? "12px" : "13px"} !important;
    font-weight: 500 !important;
    border-radius: ${isCompact ? "4px" : "8px"} !important;
    background: ${
      isCompact ? "rgba(108, 117, 125, 0.1)" : "rgba(255, 255, 255, 0.15)"
    } !important;
    border: ${
      isCompact
        ? "1px solid rgba(108, 117, 125, 0.2)"
        : "1px solid rgba(255, 255, 255, 0.4)"
    } !important;
    color: ${isCompact ? "#495057" : "#202021ff"} !important;
    transition: all 0.3s ease !important;
    height: ${isCompact ? "26px" : "32px"} !important;
    line-height: 1 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    box-shadow: ${
      isCompact
        ? "0 1px 2px rgba(0, 0, 0, 0.05)"
        : "0 2px 4px rgba(0, 0, 0, 0.1)"
    };
    vertical-align: middle !important;
  }
  
  .rbc-btn-group button:hover {
    background: ${
      isCompact ? "rgba(108, 117, 125, 0.2)" : "rgba(255, 255, 255, 0.35)"
    } !important;
    border: ${
      isCompact
        ? "1px solid rgba(108, 117, 125, 0.3)"
        : "1px solid rgba(255, 255, 255, 0.6)"
    } !important;
    transform: ${isCompact ? "none" : "translateY(-1px) scale(1.02)"};
    box-shadow: ${
      isCompact
        ? "0 2px 4px rgba(0, 0, 0, 0.1)"
        : "0 4px 8px rgba(0, 0, 0, 0.2)"
    };
    opacity: 1 !important;
  }

  /* Wide screen single-row layout adjustments - only for very large screens */
  @media (min-width: 1025px) {
    .rbc-toolbar-label {
      margin-top: 0 !important;
      flex: 1 1 auto !important;
    }
  }

  /* Responsive toolbar styles for tablets, iPad, and mobile */
  @media (max-width: 1024px) {
    .rbc-btn-group button {
      padding: 2px 6px !important;
      margin: 0 1px !important;
      font-size: 12px !important;
      font-weight: 600 !important;
      min-width: 28px !important;
      height: 28px !important;
      border-radius: 6px !important;
    }
    
    .rbc-toolbar {
      padding: 4px 8px !important;
      min-height: 36px !important;
    }
    
    .rbc-toolbar-label {
      font-size: 16px !important;
      margin-top: 4px !important;
    }
    
    /* Emoji buttons get proper sizing */
    .rbc-btn-group button[title*="View"] {
      line-height: 1 !important;
    }
  }
  
  @media (max-width: 480px) {
    .rbc-btn-group button {
      padding: 2px 4px !important;
      margin: 0 1px !important;
      font-size: 11px !important;
      min-width: 24px !important;
      height: 24px !important;
      border-radius: 4px !important;
    }
    
    .rbc-toolbar {
      padding: 3px 6px !important;
      min-height: 32px !important;
    }
    
    .rbc-toolbar-label {
      font-size: 14px !important;
    }
  }
  
  .rbc-btn-group button.rbc-active {
    background: ${
      isCompact ? "rgba(108, 117, 125, 0.3)" : "rgba(255, 255, 255, 0.95)"
    } !important;
    color: ${isCompact ? "#212529" : themeColor} !important;
    font-weight: 600 !important;
    border: ${
      isCompact
        ? "1px solid rgba(108, 117, 125, 0.4)"
        : "1px solid rgba(255, 255, 255, 0.8)"
    } !important;
    box-shadow: ${
      isCompact
        ? "0 1px 3px rgba(0, 0, 0, 0.1)"
        : "0 3px 6px rgba(0, 0, 0, 0.15)"
    };
  }
  
  .rbc-btn-group button.rbc-active:hover {
    background: ${
      isCompact ? "rgba(108, 117, 125, 0.4)" : "rgba(255, 255, 255, 1)"
    } !important;
    transform: ${isCompact ? "none" : "translateY(-1px) scale(1.02)"};
  }
  
  /* Weekend and off-hours styling for week/day views */
  
  /* Base styling for time slots */
  .rbc-time-view .rbc-time-slot {
    border-bottom: 1px solid #ddd;
  }
  
  /* Off-hours: Early morning (midnight to 7am) - first 14 timeslot groups */
  .rbc-time-view .rbc-day-slot .rbc-timeslot-group:nth-child(-n+14) .rbc-time-slot {
    background-color: rgba(255, 210, 210, 0.6) !important;
  }
  
  /* Off-hours: Late evening (8pm to midnight) - last 8 timeslot groups */
  .rbc-time-view .rbc-day-slot .rbc-timeslot-group:nth-child(n+41) .rbc-time-slot {
    background-color: rgba(255, 210, 210, 0.6) !important;
  }
  
  /* Weekend styling - use JavaScript-added classes instead of CSS-only selectors */
  .weekend-column .rbc-timeslot-group .rbc-time-slot {
    background-color: rgba(255, 180, 180, 0.4) !important;
  }
  
  .weekend-header {
    background-color: rgba(255, 180, 180, 0.5) !important;
  }
  
  /* Weekend off-hours (darker combination) */
  .weekend-column .rbc-timeslot-group:nth-child(-n+14) .rbc-time-slot,
  .weekend-column .rbc-timeslot-group:nth-child(n+41) .rbc-time-slot {
    background-color: rgba(255, 150, 150, 0.7) !important;
  }

  /* Loading indicator in cells */
  .calendar-cell-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 20px;
    margin: 2px;
  }
  
  .loading-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid #f3f3f3;
    border-top: 2px solid ${themeColor};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  /* Weekend styling */
  .weekend-column {
    background-color: #f8f8f8 !important; /* Light gray background for weekend columns */
  }
  
  .weekend-header {
    background-color: #eeeeee !important; /* Slightly darker gray for weekend headers */
    color: #666 !important;
  }
  
  .weekend-cell {
    background-color: #f8f8f8 !important; /* Light gray background for weekend cells in month view */
  }
`;

// Function to inject dynamic styles
const injectCalendarStyles = (themeColor, isCompact = false) => {
  const existingStyles = document.querySelector("#calendar-custom-styles");
  if (existingStyles) {
    existingStyles.remove();
  }

  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.id = "calendar-custom-styles";
  styleSheet.innerText = getCalendarStyles(themeColor, isCompact);
  document.head.appendChild(styleSheet);
};

class AppointmentsSlotsCalendar extends React.PureComponent {
  constructor(props) {
    super(props);
    this._isMounted = true;
    const initialDate = props.appointment?.start
      ? moment(props.appointment.start).toDate()
      : props.currentDate || new Date();

    this.state = {
      view: this.props.currentView || Views.MONTH,
      displayMode: "calendar", // 'calendar' or 'agenda' - controls visual presentation only
      batchLoadedEvents: [],
      loadingBatches: new Set(),
      scrollToTime: null, // keep a stable scroll time
      date: initialDate,
      start: undefined,
      end: undefined,
      isCompactMode: window.innerWidth <= 1024,
      isLoadingWeekData: false, // Add loading state for week/day views
      // Progressive loading for Week view
      progressiveEvents: [],
      isProgressiveLoading: false,
      progressiveBatchIndex: 0,
      // PDF generation state
      generatingPDF: false,
    };

    // Add timing tracking for debugging
    this.timingLog = {};

    // Progressive loading configuration
    this.PROGRESSIVE_BATCH_SIZE = 25; // Events per batch
    this.PROGRESSIVE_DELAY = 50; // ms between batches
  }

  // Progressive loading method for Week view - day by day
  startProgressiveLoading = (allEvents) => {
    if (!this._isMounted) return;

    devLog(
      `🔄 [PROGRESSIVE] Starting DAY-BY-DAY progressive loading with ${allEvents.length} events`,
    );

    // Group events by day of week for incremental loading
    const eventsByDay = this.groupEventsByDay(allEvents);
    devLog(
      `🔄 [PROGRESSIVE] Events grouped by day:`,
      Object.keys(eventsByDay).map(
        (day) => `${day}: ${eventsByDay[day].length} events`,
      ),
    );

    // Start with empty calendar to show grid immediately
    this.setState({
      progressiveEvents: [],
      isProgressiveLoading: true,
      progressiveBatchIndex: 0,
    });

    // Schedule day-by-day loading
    this.scheduleNextDay(eventsByDay, Object.keys(eventsByDay), 0, []);
  };

  // Group events by day of the week (0=Sunday, 1=Monday, etc.)
  groupEventsByDay = (events) => {
    const eventsByDay = {};

    events.forEach((event) => {
      const eventDate = moment(event.start);
      const dayKey = eventDate.format("dddd"); // Monday, Tuesday, etc.

      if (!eventsByDay[dayKey]) {
        eventsByDay[dayKey] = [];
      }
      eventsByDay[dayKey].push(event);
    });

    return eventsByDay;
  };

  scheduleNextDay = (eventsByDay, dayKeys, dayIndex, accumulatedEvents) => {
    if (!this._isMounted || !this.state.isProgressiveLoading) return;

    if (dayIndex >= dayKeys.length) {
      // All days loaded
      devLog(
        `🔄 [PROGRESSIVE] Completed loading all ${accumulatedEvents.length} events across ${dayKeys.length} days`,
      );
      this.setState({
        isProgressiveLoading: false,
        progressiveBatchIndex: 0,
      });
      return;
    }

    setTimeout(() => {
      if (!this._isMounted || !this.state.isProgressiveLoading) return;

      const currentDay = dayKeys[dayIndex];
      const dayEvents = eventsByDay[currentDay] || [];
      const newAccumulatedEvents = [...accumulatedEvents, ...dayEvents];

      devLog(
        `🔄 [PROGRESSIVE] Loading day ${dayIndex + 1}/${
          dayKeys.length
        }: ${currentDay} with ${dayEvents.length} events (total: ${
          newAccumulatedEvents.length
        })`,
      );

      this.setState(
        {
          progressiveEvents: newAccumulatedEvents,
          progressiveBatchIndex: dayIndex + 1,
        },
        () => {
          this.scheduleNextDay(
            eventsByDay,
            dayKeys,
            dayIndex + 1,
            newAccumulatedEvents,
          );
        },
      );
    }, this.PROGRESSIVE_DELAY);
  };

  stopProgressiveLoading = () => {
    if (this.state.isProgressiveLoading) {
      devLog(`🔄 [PROGRESSIVE] Stopping progressive loading`);
      this.setState({
        isProgressiveLoading: false,
        progressiveBatchIndex: 0,
      });
    }
  };

  // Add these helper methods to get view date ranges:
  getViewStartDate = (view, date) => {
    if (view === Views.DAY) {
      return moment(date).startOf("day");
    } else if (view === Views.WEEK) {
      return moment(date).startOf("week");
    } else if (view === Views.WORK_WEEK) {
      return moment(date).startOf("isoWeek"); // Monday start
    }
    return moment(date);
  };

  getViewEndDate = (view, date) => {
    if (view === Views.DAY) {
      return moment(date).endOf("day");
    } else if (view === Views.WEEK) {
      return moment(date).endOf("week");
    } else if (view === Views.WORK_WEEK) {
      return moment(date).startOf("isoWeek").add(4, "days").endOf("day"); // Friday end
    }
    return moment(date);
  };

  componentDidMount() {
    // Inject styles with theme color
    const { appTheme = {} } = this.props;
    const themeColor = appTheme.themeColor
      ? appTheme.themeColor.replace("0x", "#")
      : "#20a8d8";
    injectCalendarStyles(themeColor, !this.props.isTeamCalendar);

    // Add resize listener for responsive toolbar
    this.handleResize = () => {
      const isCompactMode = window.innerWidth <= 1024;
      if (this.state.isCompactMode !== isCompactMode && this._isMounted) {
        this.setState({ isCompactMode });
      }
    };
    window.addEventListener("resize", this.handleResize);

    // Notify parent of initial view
    if (this.props.onViewChange) {
      this.props.onViewChange(this.state.view);
    }

    // Add weekend styling after initial mount
    this.weekendClassTimeout = setTimeout(() => {
      if (this._isMounted) {
        this.addWeekendClasses();
      }
    }, 300);

    // Start initial batch loading for month view - but only once per session
    if (this.state.view === Views.MONTH && this.props.isTeamCalendar) {
      // Signal parent IMMEDIATELY to prevent any loading state - but NOT when slots or both filter is active
      if (
        this.props.onMonthBatchStart &&
        this.props.selectedArtifactType !== "slots" &&
        this.props.selectedArtifactType !== "appointments_slots"
      ) {
        this.props.onMonthBatchStart();
      }
      this.loadMonthInBatches(new Date());
    }

    // set initial scroll time once
    this.setState({
      scrollToTime: this.computeScrollTime(
        this.state.view,
        this.state.date,
        this.props.events,
      ),
    });
  }

  componentWillUnmount() {
    // Mark component as unmounted to prevent setState calls
    this._isMounted = false;

    // Stop progressive loading
    this.stopProgressiveLoading();

    // Remove resize listener
    if (this.handleResize) {
      window.removeEventListener("resize", this.handleResize);
    }

    // Cancel any pending timeouts
    if (this.weekendClassTimeout) {
      clearTimeout(this.weekendClassTimeout);
    }

    // Also notify parent that batch loading is complete (in case it was interrupted)
    if (this.props.onMonthBatchComplete) {
      this.props.onMonthBatchComplete();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Update styles if theme changes
    if (prevProps.appTheme !== this.props.appTheme) {
      const { appTheme = {} } = this.props;
      const themeColor = appTheme.themeColor
        ? appTheme.themeColor.replace("0x", "#")
        : "#20a8d8";
      injectCalendarStyles(themeColor, !this.props.isTeamCalendar);
    }

    // Reload batch loading if filter parameters changed in month view (but not for slots/both modes)
    if (
      this.state.view === Views.MONTH &&
      this.props.isTeamCalendar &&
      this.props.selectedArtifactType !== "slots" &&
      this.props.selectedArtifactType !== "appointments_slots" &&
      JSON.stringify(prevProps.filterParams) !==
        JSON.stringify(this.props.filterParams)
    ) {
      this.loadMonthInBatches(new Date());
    }

    // recompute scroll time only when view changes
    if (prevState.view !== this.state.view) {
      this.setState({
        scrollToTime: this.computeScrollTime(
          this.state.view,
          this.state.date,
          this.props.events,
        ),
      });
    }

    // Add weekend classes after render
    if (
      this.state.view !== prevState.view ||
      this.state.date !== prevState.date ||
      prevProps.events !== this.props.events ||
      this.state.batchLoadedEvents !== prevState.batchLoadedEvents
    ) {
      setTimeout(() => {
        if (this._isMounted) {
          this.addWeekendClasses();
        }
      }, 200);
    }
  }
  computeScrollTime = (view, date, events) => {
    // Only relevant for day/week/work_week
    if (view === Views.DAY || view === Views.WEEK || view === Views.WORK_WEEK) {
      // Filter events to only those visible in current view
      const viewStart = this.getViewStartDate(view, date);
      const viewEnd = this.getViewEndDate(view, date);

      const visibleEvents = Array.isArray(events)
        ? events.filter((event) => {
            const eventStart = moment(event.start);
            return eventStart.isBetween(viewStart, viewEnd, "day", "[]");
          })
        : [];

      if (visibleEvents.length > 0) {
        // Find the earliest event and scroll 30 minutes before it
        const sortedEvents = [...visibleEvents].sort(
          (a, b) => moment(a.start) - moment(b.start),
        );
        const firstEvent = sortedEvents[0];
        const scrollTime = moment(firstEvent.start).subtract(60, "minutes");

        // Don't scroll before 6:00 AM
        const minScrollTime = moment().startOf("day").add(7, "hours");
        return moment.max(scrollTime, minScrollTime).toDate();
      }

      // Default to 8:00 AM if no events
      return moment().startOf("day").add(8, "hours").toDate();
    }
    return undefined; // month/agenda don't need scrollToTime
  };

  onViewChange = (view) => {
    // Stop any progressive loading when switching views
    this.stopProgressiveLoading();

    this.setState({
      view,
      displayMode: "calendar", // Always reset to calendar display mode when changing views
      isLoadingWeekData: false, // Clear loading state when switching views
      progressiveEvents: [], // Clear progressive events
    });
    if (this.props.onViewChange) {
      this.props.onViewChange(view);
    }
  };

  handleNavigationAction = (action, date) => {
    const currentView = this.state.view;
    const currentDate = this.state.date || new Date();

    let newDate;

    if (action === "TODAY") {
      newDate = new Date();
    } else if (action === "PREV") {
      if (currentView === Views.MONTH) {
        newDate = moment(currentDate).subtract(1, "month").toDate();
      } else if (
        currentView === Views.WEEK ||
        currentView === Views.WORK_WEEK
      ) {
        newDate = moment(currentDate).subtract(1, "week").toDate();
      } else if (currentView === Views.DAY) {
        newDate = moment(currentDate).subtract(1, "day").toDate();
      } else if (currentView === Views.AGENDA) {
        newDate = moment(currentDate).subtract(1, "month").toDate();
      } else {
        newDate = currentDate;
      }
    } else if (action === "NEXT") {
      if (currentView === Views.MONTH) {
        newDate = moment(currentDate).add(1, "month").toDate();
      } else if (
        currentView === Views.WEEK ||
        currentView === Views.WORK_WEEK
      ) {
        newDate = moment(currentDate).add(1, "week").toDate();
      } else if (currentView === Views.DAY) {
        newDate = moment(currentDate).add(1, "day").toDate();
      } else if (currentView === Views.AGENDA) {
        newDate = moment(currentDate).add(1, "month").toDate();
      } else {
        newDate = currentDate;
      }
    } else {
      newDate = date || currentDate;
    }

    // Navigate to the new date using the current view
    this.navigateToView(currentView, newDate);
  };

  navigateToView = (view, date) => {
    if (view === Views.MONTH) {
      this.navigateMonth(view, date);
    } else if (view === Views.DAY) {
      this.navigateDay(view, date);
    } else if (view === Views.AGENDA) {
      this.navigateAgenda(view, date);
    } else if (view === Views.WEEK || view === Views.WORK_WEEK) {
      this.navigateWeek(view, date);
    } else {
      console.warn(`Unknown view type: ${view}, defaulting to month`);
      this.navigateMonth(Views.MONTH, date);
    }
  };

  navigateAgenda = (view, date) => {
    // Calculate agenda length based on current view for label formatting
    let agendaLengthDays = 31; // default to month
    if (this.state.view === Views.WEEK || this.state.view === Views.WORK_WEEK) {
      agendaLengthDays = 7;
    } else if (this.state.view === Views.DAY) {
      agendaLengthDays = 1;
    } else if (this.state.view === Views.MONTH) {
      const monthStart = moment(this.state.date || date).startOf("month");
      const monthEnd = moment(this.state.date || date).endOf("month");
      agendaLengthDays = monthEnd.diff(monthStart, "days") + 1;
    }

    // Agenda is now purely observational - just toggle display mode
    // Don't change view, date, or make any API calls
    // Just show current data in table format
    this.setState({
      displayMode: "agenda",
      calculatedAgendaLength: agendaLengthDays, // Store for label formatting
    });
  };
  onNavigate = (action, date) => {
    // Handle navigation actions (PREV, NEXT, TODAY) vs view changes
    if (action === "PREV" || action === "NEXT" || action === "TODAY") {
      this.handleNavigationAction(action, date);
    } else if (action === Views.MONTH) {
      this.navigateMonth(action, date);
    } else if (action === Views.DAY) {
      this.navigateDay(action, date);
    } else if (action === Views.AGENDA) {
      this.navigateAgenda(action, date);
    } else {
      this.navigateWeek(action, date);
    }
  };
  navigateMonth = (view, date) => {
    // Use appointment date if date is not provided and appointment exists
    const targetDate =
      date ||
      (this.props.appointment?.start
        ? moment(this.props.appointment.start).toDate()
        : new Date());

    const newMonthKey = moment(targetDate).format("YYYY-MM");

    this.setState({
      view: view,
      date: targetDate, // Use targetDate instead of date
      displayMode: "calendar",
    });

    // If changing to a different month, reset the loaded month flag
    if (this.lastLoadedMonth && this.lastLoadedMonth !== newMonthKey) {
      this.lastLoadedMonth = null;
    }

    // Only do batch loading for team calendar context
    if (this.props.isTeamCalendar) {
      // Signal parent we're handling month loading - but NOT when slots or both filter is active
      if (
        this.props.onMonthBatchStart &&
        this.props.selectedArtifactType !== "slots" &&
        this.props.selectedArtifactType !== "appointments_slots"
      ) {
        this.props.onMonthBatchStart();
      }

      // For slots or both filter, trigger container API call instead of batch loading
      if (
        this.props.selectedArtifactType === "slots" ||
        this.props.selectedArtifactType === "appointments_slots"
      ) {
        // Use strict month boundaries for counting (no padding for adjacent months)
        const monthStart = moment(targetDate).startOf("month");
        const monthEnd = moment(targetDate).endOf("month");
        const start = monthStart.format("DDMMYYYY");
        const end = monthEnd.format("DDMMYYYY");

        devLog(
          `[Calendar] 🔍 STRICT MONTH DEBUG - Selected date: ${moment(
            targetDate,
          ).format("YYYY-MM-DD")}`,
        );
        devLog(
          `[Calendar] 🔍 STRICT MONTH DEBUG - Strict month start: ${monthStart.format(
            "YYYY-MM-DD HH:mm:ss",
          )} (${start})`,
        );
        devLog(
          `[Calendar] 🔍 STRICT MONTH DEBUG - Strict month end: ${monthEnd.format(
            "YYYY-MM-DD HH:mm:ss",
          )} (${end})`,
        );
        devLog(
          `[Calendar] 🔍 STRICT MONTH DEBUG - Selected artifact type: ${this.props.selectedArtifactType}`,
        );

        if (this.props.loadAppointmentsAndSlots) {
          this.props.loadAppointmentsAndSlots(start, end, true);
        }
      } else {
        // Start batch loading for appointments using strict boundaries (no padding)
        this.loadMonthInBatches(targetDate);
      }
    } else {
      // Patient context - use strict month boundaries for counting
      const monthStart = moment(targetDate).startOf("month");
      const monthEnd = moment(targetDate).endOf("month");
      const start = monthStart.format("DDMMYYYY");
      const end = monthEnd.format("DDMMYYYY");

      devLog(
        `[Calendar] 🔍 STRICT MONTH DEBUG (Patient Context) - Selected date: ${moment(
          targetDate,
        ).format("YYYY-MM-DD")}`,
      );
      devLog(
        `[Calendar] 🔍 STRICT MONTH DEBUG (Patient Context) - Strict month start: ${monthStart.format(
          "YYYY-MM-DD HH:mm:ss",
        )} (${start})`,
      );
      devLog(
        `[Calendar] 🔍 STRICT MONTH DEBUG (Patient Context) - Strict month end: ${monthEnd.format(
          "YYYY-MM-DD HH:mm:ss",
        )} (${end})`,
      );

      if (this.props.loadAppointmentsAndSlots) {
        this.props.loadAppointmentsAndSlots(start, end, false);
      }
    }
  };

  navigateDay = (view, date) => {
    devLog(`🔍 [TIMING] DAY navigateDay called at ${Date.now()}`);
    this.timingLog.dayNavigateStart = Date.now();

    // Reset to calendar display mode
    this.setState({ displayMode: "calendar" });

    let start = moment(date).startOf("day").format("DDMMYYYY");
    // Use same date for end - API treats date range as inclusive
    let end = moment(date).endOf("day").format("DDMMYYYY");
    devLog(
      `🔍 [TIMING] DAY calling fetchAppointmentsSlots with start=${start}, end=${end}`,
    );
    this.fetchAppointmentsSlots(view, date, start, end);
  };

  navigateWeek = (view, date) => {
    devLog(`🔍 [TIMING] WEEK navigateWeek called at ${Date.now()}`);
    this.timingLog.weekNavigateStart = Date.now();

    // IMMEDIATELY start with empty progressive loading to show grid
    devLog(
      `🔄 [PROGRESSIVE] Starting IMMEDIATE progressive loading for instant grid`,
    );
    this.setState({
      displayMode: "calendar", // Reset to calendar display mode
      progressiveEvents: [], // Start with empty to show grid immediately
      isProgressiveLoading: true,
      progressiveBatchIndex: 0,
    });

    let start = moment(date).startOf("week").format("DDMMYYYY");
    // Use end of week - API treats date range as inclusive
    let end = moment(date).endOf("week").format("DDMMYYYY");
    devLog(
      `🔍 [TIMING] WEEK calling fetchAppointmentsSlots with start=${start}, end=${end}`,
    );
    this.fetchAppointmentsSlots(view, date, start, end);
  };

  loadMonthInBatches = async (date) => {
    const monthKey = moment(date).format("YYYY-MM");

    // Don't batch load when slots or both filter is active - container handles these API calls
    if (
      this.props.selectedArtifactType === "slots" ||
      this.props.selectedArtifactType === "appointments_slots"
    ) {
      return;
    }

    // Check if we're already loaded for this month
    if (this.lastLoadedMonth === monthKey) {
      return;
    }

    // Set flag to track loaded month
    this.lastLoadedMonth = monthKey;

    try {
      // Use strict month boundaries (no padding) for accurate counts and display
      const monthStart = moment(date).startOf("month");
      const monthEnd = moment(date).endOf("month");

      devLog(
        `[Calendar] 🔍 BATCH LOADING DEBUG - Using strict boundaries for ${monthKey}`,
      );
      devLog(
        `[Calendar] 🔍 BATCH LOADING DEBUG - Month start: ${monthStart.format(
          "YYYY-MM-DD",
        )}`,
      );
      devLog(
        `[Calendar] 🔍 BATCH LOADING DEBUG - Month end: ${monthEnd.format(
          "YYYY-MM-DD",
        )}`,
      );

      // Create 7-day batches
      const batches = [];
      let current = monthStart.clone();

      while (current.isBefore(monthEnd)) {
        const batchEnd = moment.min(
          current.clone().add(6, "days"),
          monthEnd.clone(),
        );
        // Add 1 day to end date since API treats end as exclusive - this ensures we get the full range
        // BUT cap at month boundary - when batchEnd is the last day of month, don't go into next month
        const apiEndDate = batchEnd.isSame(monthEnd, "day")
          ? monthEnd.clone()
          : batchEnd.clone().add(1, "day");
        const batch = {
          start: current.format("DDMMYYYY"),
          end: batchEnd.format("DDMMYYYY"),
          key: current.format("YYYY-MM-DD"),
          logicalEnd: batchEnd.format("DDMMYYYY"), // For logging the intended range
        };
        batches.push(batch);

        // Move to the day AFTER the logical batch end for next batch
        current = batchEnd.clone().add(1, "day");
      }

      // Set all batches as loading initially
      if (this._isMounted) {
        this.setState({
          batchLoadedEvents: [],
          loadingBatches: new Set(batches.map((b) => b.key)),
        });
      }

      // Load each batch and update events progressively
      let allEvents = [];
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const batchStartDate = moment(batch.start, "DDMMYYYY").format(
          "YYYY-MM-DD",
        );
        const batchEndDate = moment(batch.end, "DDMMYYYY").format("YYYY-MM-DD");
        const logicalEndDate = moment(batch.logicalEnd, "DDMMYYYY").format(
          "YYYY-MM-DD",
        );

        try {
          // Only batch load appointments - slots are handled by the container
          // Get filter params from props
          const filterParams = this.props.filterParams || {};
          const allStatus = filterParams.cancelledOnly
            ? true
            : !!filterParams.allStatus;

          // Determine which API to use based on context
          let events;

          // Check if this is a patient context
          if (isPatient(this.props.currentUser?.spaceRole)) {
            // Patient login - use patient-scoped API
            events = await PatientAppServices.loadAllAppointments(
              batch.start,
              batch.end,
            );
          } else if (this.props.patientId) {
            // Staff viewing specific patient - use regular appointments API with patientId
            events = await appointmentsService.getAllAppointments(
              filterParams,
              null, // scheduleId
              this.props.patientId, // patientId for patient detail context
              allStatus,
              batch.start,
              batch.end,
            );
          } else if (
            isStaff(this.props.currentUser?.spaceRole) &&
            this.props.isTeamCalendar
          ) {
            // Team calendar context - use calendar API
            events = await appointmentsService.getCalendarAppointments(
              {
                size: 1000,
                careTeamId: this.props.currentUser?.careTeamId,
                isCalendarView: true,
                ...filterParams,
              },
              null, // scheduleId
              null, // patientId
              allStatus,
              batch.start,
              batch.end,
              null, // seriesId
            );
          } else {
            // Default fallback - use regular appointments API
            events = await appointmentsService.getAllAppointments(
              filterParams,
              null, // scheduleId
              this.props.patientId || null, // patientId if available
              allStatus,
              batch.start,
              batch.end,
            );
          }

          // Apply client-side filtering for cancelledOnly (like container does)
          let filteredEvents = events;
          if (filterParams.cancelledOnly === true) {
            filteredEvents = events.filter(
              (event) => event.appointmentStatus === "CANCELLED",
            );
          }

          allEvents = allEvents.concat(filteredEvents);

          // Update calendar immediately after each batch loads - don't wait for all batches!
          if (this._isMounted) {
            this.setState((prevState) => {
              const newLoadingBatches = new Set(prevState.loadingBatches);
              newLoadingBatches.delete(batch.key);
              return {
                batchLoadedEvents: [...allEvents], // Show events immediately as they load
                loadingBatches: newLoadingBatches,
              };
            });
          }

          // Smaller delay so events appear faster
          if (i < batches.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 50));
          }
        } catch (error) {
          console.error(`[Calendar] Error loading batch ${i + 1}:`, error);
          // Remove from loading set even on error
          if (this._isMounted) {
            this.setState((prevState) => {
              const newLoadingBatches = new Set(prevState.loadingBatches);
              newLoadingBatches.delete(batch.key);
              return { loadingBatches: newLoadingBatches };
            });
          }
        }
      }
    } catch (error) {
      console.error("[Calendar] Error in batch loading process:", error);
    } finally {
      // Notify parent that batch loading is complete
      if (this.props.onMonthBatchComplete) {
        this.props.onMonthBatchComplete();
      }
    }
  };

  loadWeekInBatches = async (view, date, start, end) => {
    const weekKey = `${view}-${start}-${end}`;

    // Create day batches for the week
    const startMoment = moment(start, "DDMMYYYY");
    const endMoment = moment(end, "DDMMYYYY");
    const days = [];

    let current = startMoment.clone();
    while (current.isSameOrBefore(endMoment)) {
      const dayStart = current.format("DDMMYYYY");
      // Use same day for end - API treats date range as inclusive
      const dayEnd = current.format("DDMMYYYY");
      days.push({
        start: dayStart,
        end: dayEnd,
        key: current.format("YYYY-MM-DD"),
        date: current.clone(),
      });
      current.add(1, "day");
    }

    // Set all days as loading initially and clear events to start fresh
    if (this._isMounted) {
      this.setState({
        batchLoadedEvents: [],
        loadingBatches: new Set(days.map((d) => d.key)),
      });
    }

    // Load each day and update events progressively
    let allEvents = [];
    for (let i = 0; i < days.length; i++) {
      const day = days[i];

      try {
        // Determine which API to use based on context
        let events;

        // Check if this is a patient context
        if (isPatient(this.props.currentUser?.spaceRole)) {
          // Patient login - use patient-scoped API
          events = await PatientAppServices.loadAllAppointments(
            day.start,
            day.end,
          );
        } else if (this.props.patientId) {
          // Staff viewing specific patient - use regular appointments API with patientId
          events = await appointmentsService.getAllAppointments(
            {},
            null, // scheduleId
            this.props.patientId, // patientId for patient detail context
            false,
            day.start,
            day.end,
          );
        } else if (
          isStaff(this.props.currentUser?.spaceRole) &&
          this.props.isTeamCalendar
        ) {
          // Team calendar context - use calendar API
          events = await appointmentsService.getCalendarAppointments(
            {
              size: 1000,
              careTeamId: this.props.currentUser?.careTeamId,
              isCalendarView: true,
            },
            null,
            null,
            false,
            day.start,
            day.end,
            null,
          );
        } else {
          // Default fallback - use regular appointments API
          events = await appointmentsService.getAllAppointments(
            {},
            null, // scheduleId
            this.props.patientId || null, // patientId if available
            false,
            day.start,
            day.end,
          );
        }

        allEvents = allEvents.concat(events);

        // Update state with current progress
        if (this._isMounted) {
          this.setState((prevState) => {
            const newLoadingBatches = new Set(prevState.loadingBatches);
            newLoadingBatches.delete(day.key);
            return {
              batchLoadedEvents: allEvents,
              loadingBatches: newLoadingBatches,
            };
          });
        }
      } catch (error) {
        console.error(`[Calendar] Error loading day ${i + 1}:`, error);
        // Remove from loading set even on error
        if (this._isMounted) {
          this.setState((prevState) => {
            const newLoadingBatches = new Set(prevState.loadingBatches);
            newLoadingBatches.delete(day.key);
            return { loadingBatches: newLoadingBatches };
          });
        }
      }
    }

    // Final state update to ensure re-render
    if (this._isMounted) {
      this.setState((prevState) => ({
        batchLoadedEvents: allEvents,
        loadingBatches: new Set(), // Clear all loading indicators
      }));
    }
  };

  // Update the fetchAppointmentsSlots method to recalculate scroll time after data loads:
  fetchAppointmentsSlots = (view, date, start, end) => {
    devLog(
      `🔍 [TIMING] fetchAppointmentsSlots called for ${view} at ${Date.now()}`,
    );
    devLog("fetchAppointmentsSlots received:", { view, start, end });
    this.timingLog[`${view.toLowerCase()}FetchStart`] = Date.now();

    // BLOCK the full month call - this is the key fix
    if (view === Views.MONTH) {
      devLog(`🔍 [TIMING] MONTH view - setState and return`);
      this.setState({ view: view, date: date });
      return; // Don't call parent loadAppointmentsAndSlots
    }
    // disable batch loading for week views
    /*if (view === Views.WEEK || view === Views.WORK_WEEK) {
      console.log(
        `[Calendar] Using batch loading for ${view} view:`,
        start,
        end
      );
      this.setState({ view: view, date: date });
      this.loadWeekInBatches(view, date, start, end);
      return;
    }*/

    // ALWAYS update view and date immediately to show grid structure
    devLog(
      `🔍 [TIMING] ${view} - calling setState({ view, date }) at ${Date.now()}`,
    );
    this.timingLog[`${view.toLowerCase()}SetStateStart`] = Date.now();
    this.setState({ view: view, date: date });

    let { start: currentStart, end: currentEnd } = this.state;
    // For week views, always load even if date range is the same (bypass cache)
    if (
      start !== currentStart ||
      end !== currentEnd ||
      view === Views.WEEK ||
      view === Views.WORK_WEEK
    ) {
      const rangeKey = `${start}-${end}`;

      // Prevent rapid duplicate calls for the same range, but NOT for week views
      if (
        view !== Views.WEEK &&
        view !== Views.WORK_WEEK &&
        this.lastRangeKey === rangeKey &&
        Date.now() - this.lastRangeTime < 1000
      ) {
        return;
      }

      this.lastRangeKey = rangeKey;
      this.lastRangeTime = Date.now();

      // Update start/end state and trigger data loading
      devLog(
        `🔍 [TIMING] ${view} - calling setState with API data at ${Date.now()}`,
      );
      this.timingLog[`${view.toLowerCase()}ApiCallStart`] = Date.now();
      this.setState(
        {
          start,
          end,
          isLoadingWeekData: true, // Set loading state for week/day views
        },
        this.props.loadAppointmentsAndSlots(
          start,
          end,
          false,
          view.toLowerCase(),
        ),
      );
    }
  };

  fetchAppointmentsSlotsBAK = (view, date, start, end) => {
    // BLOCK the full month call - this is the key fix
    if (view === Views.MONTH) {
      this.setState({ view: view, date: date });
      return; // Don't call parent loadAppointmentsAndSlots
    }

    // TEMPORARILY disable batch loading for week views to test
    // if (view === Views.WEEK || view === Views.WORK_WEEK) {
    //   console.log(`[Calendar] Using batch loading for ${view} view:`, start, end);
    //   this.setState({ view: view, date: date });
    //   this.loadWeekInBatches(view, date, start, end);
    //   return;
    // }

    // ALWAYS update view and date immediately to show grid structure
    this.setState({ view: view, date: date });

    let { start: currentStart, end: currentEnd } = this.state;
    // For week views, always load even if date range is the same (bypass cache)
    if (
      start !== currentStart ||
      end !== currentEnd ||
      view === Views.WEEK ||
      view === Views.WORK_WEEK
    ) {
      const rangeKey = `${start}-${end}`;

      // Prevent rapid duplicate calls for the same range, but NOT for week views
      // Week views should always load fresh data
      if (
        view !== Views.WEEK &&
        view !== Views.WORK_WEEK &&
        this.lastRangeKey === rangeKey &&
        Date.now() - this.lastRangeTime < 1000
      ) {
        return;
      }

      this.lastRangeKey = rangeKey;
      this.lastRangeTime = Date.now();

      // Update start/end state and trigger data loading
      this.setState(
        {
          start,
          end,
        },
        () => {
          // Use context-aware API routing
          if (isPatient(this.props.currentUser?.spaceRole)) {
            // Patient context - make API call with proper date range
            devLog(
              "[Calendar] Patient context - making API call for",
              view,
              "view:",
              start,
              "to",
              end,
            );
            this.props.loadAppointmentsAndSlots(start, end, false);
          } else {
            // Staff context - call container which will route appropriately
            this.props.loadAppointmentsAndSlots(start, end, false);
          }
        },
      );
    }
  };

  onSelectEvent = (event, nativeEvent) => {
    if (isItCountSlot(event)) {
      let date = event.start;
      let start = moment(date).startOf("day").format("DDMMYYYY");
      let end = moment(date).endOf("day").format("DDMMYYYY");
      this.fetchAppointmentsSlots(Views.DAY, date, start, end);
      return;
    }
    if (this.props.onSelectEvent) {
      this.props.onSelectEvent(event, nativeEvent);
    }
  };

  componentDidUpdateBAK(prevProps, prevState) {
    // Add weekend classes to DOM elements after render
    if (
      this.state.view !== prevState.view ||
      this.state.date !== prevState.date ||
      prevProps.events !== this.props.events ||
      this.state.batchLoadedEvents !== prevState.batchLoadedEvents
    ) {
      setTimeout(() => {
        if (this._isMounted) {
          this.addWeekendClasses();
        }
      }, 200); // Longer delay for DOM to settle
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Track when events change
    if (prevProps.events !== this.props.events) {
      const view = this.state.view.toLowerCase();
      this.timingLog[`${view}EventsReceived`] = Date.now();
      devLog(
        `🔍 [TIMING] ${this.state.view} - Events changed at ${Date.now()}`,
      );
      devLog(
        `🔍 [TIMING] ${this.state.view} - Previous events:`,
        prevProps.events?.length || 0,
      );
      devLog(
        `🔍 [TIMING] ${this.state.view} - New events:`,
        this.props.events?.length || 0,
      );

      if (this.timingLog[`${view}ApiCallStart`]) {
        const apiTime =
          this.timingLog[`${view}EventsReceived`] -
          this.timingLog[`${view}ApiCallStart`];
        devLog(
          `🔍 [TIMING] ${this.state.view} - API response time: ${apiTime}ms`,
        );
      }

      // Start progressive loading for Week view when events arrive - but only if there are events
      if (
        (this.state.view === Views.WEEK ||
          this.state.view === Views.WORK_WEEK) &&
        this.props.events &&
        this.props.events.length > 0 &&
        this.state.isProgressiveLoading &&
        this.state.progressiveEvents.length === 0 // Only if we're still showing empty grid
      ) {
        devLog(
          `🔄 [PROGRESSIVE] Events arrived! Starting DAY-BY-DAY loading with ${this.props.events.length} events`,
        );
        // Group events by day and start day-by-day loading
        const eventsByDay = this.groupEventsByDay(this.props.events);
        this.scheduleNextDay(eventsByDay, Object.keys(eventsByDay), 0, []);
      }
    }

    // Update styles if theme changes
    if (prevProps.appTheme !== this.props.appTheme) {
      const { appTheme = {} } = this.props;
      const themeColor = appTheme.themeColor
        ? appTheme.themeColor.replace("0x", "#")
        : "#20a8d8";
      injectCalendarStyles(themeColor, !this.props.isTeamCalendar);
    }

    // Reload batch loading if filter parameters changed in month view (but not for slots/both modes)
    if (
      this.state.view === Views.MONTH &&
      this.props.isTeamCalendar &&
      this.props.selectedArtifactType !== "slots" &&
      this.props.selectedArtifactType !== "appointments_slots" &&
      JSON.stringify(prevProps.filterParams) !==
        JSON.stringify(this.props.filterParams)
    ) {
      this.loadMonthInBatches(new Date());
    }

    // Clear loading state when events arrive for week/day/agenda views
    if (
      this.state.isLoadingWeekData &&
      prevProps.events !== this.props.events &&
      this.props.events &&
      this.props.events.length >= 0 &&
      (this.state.view === Views.WEEK ||
        this.state.view === Views.WORK_WEEK ||
        this.state.view === Views.DAY ||
        this.state.view === Views.AGENDA)
    ) {
      devLog(
        `🔍 [TIMING] ${
          this.state.view
        } - Clearing loading state at ${Date.now()} - events: ${
          this.props.events?.length || 0
        }`,
      );
      this.setState({ isLoadingWeekData: false });
    }

    // Recalculate scroll time when view changes OR when events change
    if (
      prevState.view !== this.state.view ||
      prevProps.events !== this.props.events
    ) {
      const newScrollTime = this.computeScrollTime(
        this.state.view,
        this.state.date,
        this.props.events,
      );

      if (newScrollTime && newScrollTime !== this.state.scrollToTime) {
        this.setState({ scrollToTime: newScrollTime });
      }
    }

    // Add weekend classes after render
    if (
      this.state.view !== prevState.view ||
      this.state.date !== prevState.date ||
      prevProps.events !== this.props.events ||
      this.state.batchLoadedEvents !== prevState.batchLoadedEvents
    ) {
      setTimeout(() => {
        if (this._isMounted) {
          this.addWeekendClasses();
        }
      }, 200);
    }
  }

  addWeekendClasses = () => {
    const { view, date } = this.state;

    // Remove existing classes
    document
      .querySelectorAll(".weekend-column, .weekend-header, .weekend-cell")
      .forEach((el) => {
        el.classList.remove("weekend-column", "weekend-header", "weekend-cell");
      });

    if (view === Views.WEEK) {
      // For week view, find actual weekend columns based on dates
      const headers = document.querySelectorAll(
        ".rbc-time-view .rbc-time-header-content .rbc-header",
      );
      const daySlots = document.querySelectorAll(
        ".rbc-time-view .rbc-time-content > .rbc-day-slot",
      );

      headers.forEach((header, index) => {
        const daySlot = daySlots[index];
        if (!daySlot) return;

        // Calculate the actual date for this column (full week: Sunday to Saturday)
        const columnDate = moment(date).startOf("week").add(index, "days");

        // Check if this column represents a weekend day (Saturday=6, Sunday=0)
        if (columnDate && (columnDate.day() === 0 || columnDate.day() === 6)) {
          header.classList.add("weekend-header");
          daySlot.classList.add("weekend-column");
        }
      });
    } else if (view === Views.MONTH) {
      // For month view, highlight weekend cells
      const monthCells = document.querySelectorAll(
        ".rbc-month-view .rbc-date-cell",
      );

      monthCells.forEach((cell, index) => {
        // Try multiple selectors for the date button
        let dateButton =
          cell.querySelector(".rbc-button-link") ||
          cell.querySelector("button") ||
          cell.querySelector("[role='button']");

        if (dateButton) {
          const dateText = dateButton.textContent;
          if (dateText && date) {
            try {
              const dayNum = parseInt(dateText);
              if (!isNaN(dayNum)) {
                // Create a date for this cell within the current month
                const cellDate = moment(date).date(dayNum);

                // Check if it's a weekend (Saturday=6, Sunday=0)
                if (cellDate.day() === 0 || cellDate.day() === 6) {
                  cell.classList.add("weekend-cell");
                }
              }
            } catch (e) {}
          }
        }
      });
    }
  };

  downloadAgendaAsPDF = async () => {
    try {
      this.setState({ generatingPDF: true });
      devLog("Starting PDF generation...");

      // Get the events data directly instead of trying to capture the massive table
      const { view, date } = this.state;
      const currentDate = date || new Date();
      const startDate = moment(currentDate).startOf("month").format("MMM DD");
      const endDate = moment(currentDate).endOf("month").format("MMM DD, YYYY");

      // Get events from the current display
      const events = this.getDisplayEvents();

      if (!events || events.length === 0) {
        alert("No appointments found to export.");
        this.setState({ generatingPDF: false });
        return;
      }

      devLog(`Creating PDF for ${events.length} appointments`);

      // Create PDF with text-based content instead of image capture
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const usableWidth = pageWidth - margin * 2;
      const usableHeight = pageHeight - margin * 2;

      // Add title
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text(
        `Appointment Agenda - ${startDate} to ${endDate}`,
        margin,
        margin + 10,
      );

      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      pdf.text(`Total Appointments: ${events.length}`, margin, margin + 20);

      // Table headers
      const headers = [
        "Date",
        "Time",
        "Patient",
        "Description",
        "With/Booked-by",
        "Status",
      ];
      const colWidths = [25, 30, 50, 70, 40, 25]; // mm
      let startX = margin;
      let currentY = margin + 35;

      // Draw headers
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);

      headers.forEach((header, index) => {
        pdf.rect(startX, currentY - 5, colWidths[index], 8);
        pdf.text(header, startX + 2, currentY);
        startX += colWidths[index];
      });

      currentY += 10;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);

      // Group events by date
      const groupedEvents = {};
      events.forEach((event) => {
        const dateKey = moment(event.start).format("YYYY-MM-DD");
        if (!groupedEvents[dateKey]) {
          groupedEvents[dateKey] = [];
        }
        groupedEvents[dateKey].push(event);
      });

      const sortedDates = Object.keys(groupedEvents).sort();

      for (const dateKey of sortedDates) {
        const dayEvents = groupedEvents[dateKey];

        for (let index = 0; index < dayEvents.length; index++) {
          const event = dayEvents[index];

          // Check if we need a new page
          if (currentY > pageHeight - 20) {
            pdf.addPage();
            currentY = margin + 10;
          }

          // Extract appointment data
          const patientParticipant = event.participants?.find(
            (p) => p.referenceType === "PATIENT",
          );
          const staffParticipant = event.participants?.find(
            (p) => p.referenceType === "STAFF",
          );
          const patientName =
            patientParticipant?.displayHumanName?.text ||
            event.title ||
            I18nProvider.getIntlProvider().formatMessage(
              Messages.unknown_patient,
            );
          const description =
            event.appointmentDescription || event.description || "";
          const bookedBy = event.createdByUser;
          const staffName = staffParticipant?.displayHumanName?.text;
          const appointmentStatus = event.appointmentStatus || "SCHEDULED";

          // Prepare row data
          const rowData = [
            index === 0 ? moment(dateKey).format("MMM DD") : "", // Date only for first event of day
            `${moment(event.start).format("HH:mm")}-${moment(event.end).format(
              "HH:mm",
            )}`,
            patientName.length > 25
              ? patientName.substring(0, 22) + "..."
              : patientName,
            description.length > 35
              ? description.substring(0, 32) + "..."
              : description,
            staffName
              ? staffName.substring(0, 20) +
                (staffName.length > 20 ? "..." : "")
              : bookedBy
                ? bookedBy.split("@")[0]
                : "",
            appointmentStatus,
          ];

          // Draw row
          startX = margin;
          rowData.forEach((data, colIndex) => {
            pdf.rect(startX, currentY - 5, colWidths[colIndex], 8);
            if (data) {
              pdf.text(data, startX + 1, currentY);
            }
            startX += colWidths[colIndex];
          });

          currentY += 8;
        }
      }

      // Add footer
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth - 30, pageHeight - 5);
        pdf.text(
          `Generated: ${moment().format("MMM DD, YYYY HH:mm")}`,
          margin,
          pageHeight - 5,
        );
      }

      const fileName = `Agenda-${moment(currentDate)
        .startOf("month")
        .format("MMM-DD")}-to-${moment(currentDate)
        .endOf("month")
        .format("MMM-DD-YYYY")}.pdf`;

      devLog("Saving PDF:", fileName);
      pdf.save(fileName);
      devLog("PDF generation completed successfully");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(
        "An error occurred while generating the PDF. Please check the console for details.",
      );
    } finally {
      this.setState({ generatingPDF: false });
    }
  };

  // Helper method to get current display events
  getDisplayEvents = () => {
    const { view } = this.state;
    let { events } = this.props;

    // Use the same logic as render method to get display events
    if (
      this.props.isTeamCalendar &&
      view === Views.MONTH &&
      this.state.batchLoadedEvents
    ) {
      return this.state.batchLoadedEvents;
    } else if (
      view === Views.WEEK &&
      this.state.isProgressiveLoading &&
      this.state.progressiveEvents
    ) {
      return this.state.progressiveEvents;
    } else {
      return Array.isArray(events) ? events : [];
    }
  };

  renderEventWithView = (eventProps) => {
    // Reads this.state.view at call time because the parent DnD HOC caches the
    // components prop on mount (withDragAndDrop.js), so a closure-captured view
    // would be stuck at the initial value.
    const parentEvent = this.props.components && this.props.components.event;
    if (!parentEvent) return null;
    return parentEvent({ ...eventProps, view: this.state.view });
  };

  render() {
    const renderStart = Date.now();
    let view = this.state.view;

    // Validate view - ensure it's a valid calendar view
    const validViews = [
      Views.MONTH,
      Views.WEEK,
      Views.WORK_WEEK,
      Views.DAY,
      Views.AGENDA,
    ];
    if (!validViews.includes(view)) {
      console.warn(`Invalid view "${view}" detected, defaulting to month view`);
      view = Views.MONTH;
      // Update state to correct view
      this.setState({ view: Views.MONTH });
    }

    devLog(`🔍 [TIMING] ${view} - render() called at ${renderStart}`);

    let { batchLoadedEvents, loadingBatches, scrollToTime, date } = this.state;
    let { events, appTheme = {} } = this.props;

    // For patient context, always use props events since we make fresh API calls
    // For staff month view with team calendar, use batch-loaded events
    // For Week view with many events, use progressive loading
    let displayEvents;
    if (isPatient(this.props.currentUser?.spaceRole)) {
      // Patient context - always use props events (fresh API calls with proper date ranges)
      displayEvents = Array.isArray(events) ? events : [];
    } else if (view === Views.MONTH && this.props.isTeamCalendar) {
      // Staff team calendar month view - use batch loading for appointments only

      if (
        this.props.selectedArtifactType === "slots" ||
        this.props.selectedArtifactType === "appointments_slots"
      ) {
        // For slots, use props events since batch loading is skipped
        displayEvents = Array.isArray(events) ? events : [];
        devLog(
          `🔄 [PROGRESSIVE] Using ${displayEvents.length} progressive events for rendering (progressive loading active)`,
        );
      } else if (
        batchLoadedEvents.length === 0 &&
        Array.isArray(events) &&
        events.length > 0
      ) {
        displayEvents = events;
        devLog(
          `🔄 [PROGRESSIVE] Using ${displayEvents.length} progressive events for rendering (progressive loading active)`,
        );
      } else {
        displayEvents = batchLoadedEvents || [];
        devLog(
          `🔄 [PROGRESSIVE] Using ${displayEvents.length} progressive events for rendering (progressive loading active)`,
        );
      }
    } else if (
      (view === Views.WEEK || view === Views.WORK_WEEK) &&
      this.state.isProgressiveLoading
    ) {
      // Week view with progressive loading active - use progressive events (even if empty for grid)
      displayEvents = this.state.progressiveEvents;
      devLog(
        `🔄 [PROGRESSIVE] Using ${displayEvents.length} progressive events for rendering (progressive loading active)`,
      );
    } else {
      // Staff non-month views (Day/Week) or single patient view
      // Just use props events - Agenda display mode will use whatever is current
      displayEvents = Array.isArray(events) ? events : [];
      devLog(
        `🔄 [PROGRESSIVE] Using ${displayEvents.length} props events for ${view}`,
      );
    }

    // Custom Agenda View Component for full control
    const CustomAgendaView = ({ events, date, length, onSelectEvent }) => {
      devLog("CustomAgendaView rendering with:", {
        eventsCount: events.length,
        date,
        length,
      });

      const groupedEvents = {};

      // Group events by date
      events.forEach((event) => {
        const dateKey = moment(event.start).format("YYYY-MM-DD");
        if (!groupedEvents[dateKey]) {
          groupedEvents[dateKey] = [];
        }
        groupedEvents[dateKey].push(event);
      });

      const sortedDates = Object.keys(groupedEvents).sort();

      return (
        <div
          className="rbc-agenda-view"
          style={{ height: "100%", overflow: "auto" }}
        >
          <table className="rbc-agenda-table table table-hover">
            <thead>
              <tr>
                <th style={{ width: "110px", minWidth: "110px" }}>DATE</th>
                <th style={{ width: "130px", minWidth: "130px" }}>TIME</th>
                <th style={{ width: "220px", minWidth: "220px" }}>PATIENT</th>
                <th>DESCRIPTION</th>
                <th style={{ width: "150px", minWidth: "150px" }}>
                  WITH/BOOKED-BY
                </th>
                <th style={{ width: "120px", minWidth: "120px" }}>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {sortedDates.map((dateKey) => {
                const dayEvents = groupedEvents[dateKey];
                const displayDate = moment(dateKey).format("ddd MMM DD");

                return dayEvents.map((event, index) => {
                  // Extract appointment data - find the PATIENT and STAFF participants
                  const patientParticipant = event.participants?.find(
                    (p) => p.referenceType === "PATIENT",
                  );
                  const staffParticipant = event.participants?.find(
                    (p) => p.referenceType === "STAFF",
                  );
                  const patientName =
                    patientParticipant?.displayHumanName?.text ||
                    event.title ||
                    I18nProvider.getIntlProvider().formatMessage(
                      Messages.unknown_patient,
                    );
                  const mrnNumber = patientParticipant?.mrnNumber;
                  const phoneNumber =
                    patientParticipant?.phoneNumber ||
                    patientParticipant?.contact;
                  const patientEmail = patientParticipant?.email;
                  const description =
                    event.appointmentDescription || event.description || "";
                  const colorCode = event.colorCode || "#007bff";
                  const appointmentStatus = event.appointmentStatus;
                  const type = event.type;
                  const duration =
                    event.minutesDuration ||
                    (event.end - event.start) / (1000 * 60);
                  const isRecurring = !!event.recurrence;
                  const bookedBy = event.createdByUser;
                  const staffName = staffParticipant?.displayHumanName?.text;
                  const location = event.location || event.service;

                  return (
                    <tr
                      key={`${dateKey}-${index}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => onSelectEvent && onSelectEvent(event)}
                      className="agenda-row"
                    >
                      {/* Date cell - only show for first event of the day */}
                      <td
                        className="rbc-agenda-date-cell"
                        style={{ verticalAlign: "top", paddingTop: "12px" }}
                      >
                        {index === 0 && (
                          <div style={{ fontWeight: "500", color: "#495057" }}>
                            {displayDate}
                          </div>
                        )}
                      </td>

                      {/* Time cell */}
                      <td
                        className="rbc-agenda-time-cell"
                        style={{ verticalAlign: "top", paddingTop: "12px" }}
                      >
                        <div
                          style={{
                            fontWeight: "400",
                            color: "#6c757d",
                            fontSize: "0.9em",
                          }}
                        >
                          {moment(event.start).format("h:mm A")} –{" "}
                          {moment(event.end).format("h:mm A")}
                        </div>
                        {duration > 0 && (
                          <div
                            style={{
                              fontSize: "0.75em",
                              color: "#6c757d",
                              marginTop: "2px",
                            }}
                          >
                            ⏱️ {duration}min
                          </div>
                        )}
                      </td>

                      {/* Patient Info */}
                      <td style={{ verticalAlign: "top", paddingTop: "8px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: "8px",
                          }}
                        >
                          {/* Color indicator */}
                          <div
                            style={{
                              width: "4px",
                              height: "45px",
                              backgroundColor: colorCode,
                              borderRadius: "2px",
                              flexShrink: 0,
                              marginTop: "2px",
                            }}
                          />

                          <div style={{ flex: 1, minWidth: 0 }}>
                            {/* Patient name */}
                            <div
                              style={{
                                fontWeight: "500",
                                color: "#212529",
                                fontSize: "0.95em",
                                marginBottom: "2px",
                              }}
                            >
                              {patientName}
                            </div>

                            {/* MRN */}
                            {mrnNumber && (
                              <div
                                style={{
                                  color: "#6c757d",
                                  fontSize: "0.8em",
                                  marginBottom: "2px",
                                }}
                              >
                                MRN: {mrnNumber}
                              </div>
                            )}

                            {/* Phone */}
                            {phoneNumber && (
                              <div
                                style={{
                                  color: "#6c757d",
                                  fontSize: "0.8em",
                                }}
                              >
                                📞 {phoneNumber}
                              </div>
                            )}

                            {/* Email */}
                            {patientEmail && (
                              <div
                                style={{
                                  color: "#6c757d",
                                  fontSize: "0.8em",
                                }}
                              >
                                ✉️ {patientEmail}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Description & Details */}
                      <td style={{ verticalAlign: "top", paddingTop: "8px" }}>
                        {/* Description */}
                        {description && (
                          <div
                            style={{
                              color: "#495057",
                              fontSize: "0.9em",
                              marginBottom: "4px",
                              fontWeight: "500",
                            }}
                          >
                            {description}
                          </div>
                        )}

                        {/* Location */}
                        {location && (
                          <div
                            style={{
                              color: "#6c757d",
                              fontSize: "0.8em",
                              marginBottom: "4px",
                            }}
                          >
                            📍 {location}
                          </div>
                        )}

                        {/* Additional details in horizontal layout */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "0.8em",
                            color: "#6c757d",
                            flexWrap: "wrap",
                          }}
                        >
                          {/* Type indicator */}
                          {type && type !== "NORMAL" && (
                            <span
                              style={{
                                backgroundColor:
                                  type === "URGENT" ? "#dc3545" : "#17a2b8",
                                color: "white",
                                padding: "1px 6px",
                                borderRadius: "8px",
                                fontSize: "0.75em",
                                fontWeight: "500",
                              }}
                            >
                              {type}
                            </span>
                          )}

                          {/* Recurring indicator */}
                          {isRecurring && (
                            <span
                              title={I18nProvider.getIntlProvider().formatMessage(
                                Messages.recurring_appointment_title,
                              )}
                            >
                              🔄{" "}
                              <Text content={Messages.recurring_appointment} />
                            </span>
                          )}
                        </div>
                      </td>

                      {/* With/Booked-by Column */}
                      <td style={{ verticalAlign: "top", paddingTop: "12px" }}>
                        {/* Staff participant (With) */}
                        {staffName && (
                          <div
                            style={{
                              color: "#495057",
                              fontSize: "0.85em",
                              fontWeight: "500",
                              marginBottom: "4px",
                            }}
                          >
                            👩‍⚕️ {staffName.trim()}
                          </div>
                        )}

                        {/* Booked by */}
                        {bookedBy && (
                          <div
                            style={{
                              color: "#6c757d",
                              fontSize: "0.8em",
                            }}
                          >
                            📝 {bookedBy.split("@")[0]}
                          </div>
                        )}
                      </td>

                      {/* Status Column */}
                      <td style={{ verticalAlign: "top", paddingTop: "12px" }}>
                        {/* Status indicator */}
                        {appointmentStatus && (
                          <span
                            style={{
                              fontSize: "0.75em",
                              padding: "4px 8px",
                              borderRadius: "12px",
                              backgroundColor:
                                appointmentStatus === "CANCELLED"
                                  ? "#f8d7da"
                                  : appointmentStatus === "COMPLETED"
                                    ? "#d4edda"
                                    : appointmentStatus === "CONFIRMED"
                                      ? "#d1ecf1"
                                      : appointmentStatus === "SCHEDULED"
                                        ? "#e2e3e5"
                                        : "#f1f3f4",
                              color:
                                appointmentStatus === "CANCELLED"
                                  ? "#721c24"
                                  : appointmentStatus === "COMPLETED"
                                    ? "#155724"
                                    : appointmentStatus === "CONFIRMED"
                                      ? "#0c5460"
                                      : appointmentStatus === "SCHEDULED"
                                        ? "#495057"
                                        : "#495057",
                              fontWeight: "500",
                              display: "inline-block",
                            }}
                          >
                            {appointmentStatus}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>
          </table>
        </div>
      );
    };

    // Custom toolbar component with glassmorphic styling and appointment counter
    const CustomToolbar = ({ date, view, onNavigate, onView, label }) => {
      // Use state for responsive mode
      const isCompactMode = this.state.isCompactMode;

      // Determine which events to use for counting
      const getEventsForCount = () => {
        if (isPatient(this.props.currentUser?.spaceRole)) {
          return this.props.events || [];
        }

        if (view === Views.MONTH && this.props.isTeamCalendar) {
          const isSlotMode =
            this.props.selectedArtifactType === "slots" ||
            this.props.selectedArtifactType === "appointments_slots";

          if (
            isSlotMode ||
            (this.state.batchLoadedEvents.length === 0 &&
              this.props.events?.length > 0)
          ) {
            return this.props.events || [];
          }
          return this.state.batchLoadedEvents || [];
        }

        return this.props.events || [];
      };

      const eventsForCount = getEventsForCount();

      // Calculate counts
      let appointmentCount = 0;
      let slotsCount = 0;

      if (this.props.selectedArtifactType === "slots") {
        // Count slots (handle both aggregated count slots and individual slots)
        slotsCount = (displayEvents || []).reduce((total, event) => {
          return total + (isItCountSlot(event) ? event.count || 0 : 1);
        }, 0);
      } else if (this.props.selectedArtifactType !== "appointments_slots") {
        appointmentCount = eventsForCount.length;
      }

      // Simple loading check
      const isLoading =
        this.state.isLoadingWeekData ||
        (view === Views.MONTH && this.state.loadingBatches.size > 0);
      const hasNoData = appointmentCount === 0 && slotsCount === 0;
      const showLoadingSpinner = isLoading && hasNoData;

      const themeColor = appTheme.themeColor?.replace("0x", "#") || "#20a8d8";

      // Build the counter display
      const counterContent = showLoadingSpinner ? (
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <i className="fa fa-spinner fa-spin" style={{ fontSize: "14px" }} />
          <span style={{ fontSize: "0.9em", color: "#6c757d" }}>
            Loading...
          </span>
        </span>
      ) : this.props.selectedArtifactType === "slots" ? (
        `${this.props.isTeamCalendar ? "🟢 " : ""}${slotsCount}${
          !this.props.isTeamCalendar ? " slot(s)" : ""
        }`
      ) : (
        `${this.props.isTeamCalendar ? "🏥 " : ""}${appointmentCount}${
          !this.props.isTeamCalendar
            ? window.location.pathname.includes("/new-booking") ||
              (displayEvents?.length > 0 &&
                displayEvents[0].slotStatus !== undefined)
              ? " slot(s)"
              : " appt(s)"
            : ""
        }`
      );

      const labelContent = (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "16px",
            flexShrink: 0,
          }}
        >
          <span>
            {this.props.isTeamCalendar && "📅 "}
            {formatCompactDateRange(
              this.props.isTeamCalendar
                ? label
                : label?.replace(/(\w{3})\w*\s(\d{4})/, "$1 $2"),
              view,
              date,
              isCompactMode,
              !this.props.isTeamCalendar,
              this.state.calculatedAgendaLength,
            )}
          </span>
          {this.props.selectedArtifactType !== "appointments_slots" && (
            <span
              style={{
                fontSize: "14px",
                fontWeight: "500",
                background: "rgba(255,255,255,0.25)",
                padding: "4px 10px",
                borderRadius: "12px",
                whiteSpace: "nowrap",
                border: "1px solid rgba(255,255,255,0.3)",
                backdropFilter: "blur(5px)",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {counterContent}
            </span>
          )}
        </div>
      );

      return (
        <div
          className="rbc-toolbar"
          style={{ flexDirection: "column", alignItems: "stretch" }}
        >
          {!isCompactMode ? (
            // Wide screen: single row layout with left nav, center label, right views
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              {/* Left: Navigation buttons */}
              <span className="rbc-btn-group" style={{ flex: "0 0 auto" }}>
                <button type="button" onClick={() => onNavigate("TODAY")}>
                  {view === "day" ? (
                    <Text content={Messages.today} />
                  ) : view === "month" ? (
                    <Text content={Messages.current_month} />
                  ) : (
                    <Text content={Messages.current_week} />
                  )}
                </button>
                <button type="button" onClick={() => onNavigate("PREV")}>
                  ‹ Back
                </button>
                <button type="button" onClick={() => onNavigate("NEXT")}>
                  Next ›
                </button>
              </span>

              {/* Center: Label content */}
              <div
                className="rbc-toolbar-label"
                style={{
                  flex: "1 1 auto",
                  textAlign: "center",
                  minWidth: 0, // Allow flex shrinking
                }}
              >
                {labelContent}
              </div>

              {/* Right: View buttons */}
              <span className="rbc-btn-group" style={{ flex: "0 0 auto" }}>
                <button
                  type="button"
                  className={view === "month" ? "rbc-active" : ""}
                  onClick={() => onView("month")}
                >
                  Month
                </button>
                <button
                  type="button"
                  className={view === "week" ? "rbc-active" : ""}
                  onClick={() => onView("week")}
                >
                  Week
                </button>
                <button
                  type="button"
                  className={view === "work_week" ? "rbc-active" : ""}
                  onClick={() => onView("work_week")}
                >
                  Work Week
                </button>
                <button
                  type="button"
                  className={view === "day" ? "rbc-active" : ""}
                  onClick={() => onView("day")}
                >
                  Day
                </button>
                {!this.props.hideAgenda && (
                  <button
                    type="button"
                    className={
                      this.state.displayMode === "agenda" ? "rbc-active" : ""
                    }
                    onClick={() => {
                      onView("agenda");
                      // Don't call onViewChange - Agenda is display mode only
                    }}
                  >
                    Agenda
                  </button>
                )}
              </span>

              {/* PDF Download button - only show in agenda display mode */}
              {this.state.displayMode === "agenda" && (
                <span
                  className="rbc-btn-group"
                  style={{ flex: "0 0 auto", marginLeft: "12px" }}
                >
                  <button
                    type="button"
                    onClick={this.downloadAgendaAsPDF}
                    disabled={this.state.generatingPDF}
                    style={{
                      backgroundColor: "#007bff",
                      color: "white",
                      border: "1px solid #007bff",
                      borderRadius: "4px",
                      padding: "6px 12px",
                      fontSize: "13px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    {this.state.generatingPDF ? (
                      <>
                        <div
                          style={{
                            width: "12px",
                            height: "12px",
                            border: "2px solid rgba(255,255,255,0.3)",
                            borderTop: "2px solid white",
                            borderRadius: "50%",
                            animation: "calendar-spinner 0.8s linear infinite",
                          }}
                        />
                        Generating...
                      </>
                    ) : (
                      <>⬇️ PDF</>
                    )}
                  </button>
                </span>
              )}
            </div>
          ) : (
            // Compact mode: two-row layout
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <span className="rbc-btn-group">
                  <button type="button" onClick={() => onNavigate("TODAY")}>
                    Now
                  </button>
                  <button type="button" onClick={() => onNavigate("PREV")}>
                    ‹
                  </button>
                  <button type="button" onClick={() => onNavigate("NEXT")}>
                    ›
                  </button>
                </span>

                <span className="rbc-btn-group">
                  <button
                    type="button"
                    className={view === "month" ? "rbc-active" : ""}
                    onClick={() => onView("month")}
                    title="Month View"
                  >
                    M
                  </button>
                  <button
                    type="button"
                    className={view === "week" ? "rbc-active" : ""}
                    onClick={() => onView("week")}
                    title="Week View"
                  >
                    W
                  </button>
                  <button
                    type="button"
                    className={view === "work_week" ? "rbc-active" : ""}
                    onClick={() => onView("work_week")}
                    title="Work Week View"
                  >
                    WD
                  </button>
                  <button
                    type="button"
                    className={view === "day" ? "rbc-active" : ""}
                    onClick={() => onView("day")}
                    title="Day View"
                  >
                    D
                  </button>
                  {!this.props.hideAgenda && (
                    <button
                      type="button"
                      className={
                        this.state.displayMode === "agenda" ? "rbc-active" : ""
                      }
                      onClick={() => {
                        onView("agenda");
                        // Don't call onViewChange - Agenda is display mode only
                      }}
                      title="Agenda View"
                    >
                      📋
                    </button>
                  )}
                  {/* PDF Download button for compact mode - only show in agenda display mode */}
                  {this.state.displayMode === "agenda" && (
                    <button
                      type="button"
                      onClick={this.downloadAgendaAsPDF}
                      disabled={this.state.generatingPDF}
                      title={I18nProvider.getIntlProvider().formatMessage(
                        Messages.download_agenda_pdf,
                      )}
                      style={{
                        backgroundColor: "#007bff",
                        color: "white",
                        border: "1px solid #007bff",
                        fontSize: "12px",
                        marginLeft: "4px",
                      }}
                    >
                      {this.state.generatingPDF ? "⏳" : "⬇️"}
                    </button>
                  )}
                </span>
              </div>

              {/* Second row: Label content for compact mode */}
              <div
                className="rbc-toolbar-label"
                style={{ textAlign: "center", marginTop: "8px" }}
              >
                {labelContent}
              </div>
            </>
          )}
        </div>
      );
    };

    // Custom date cell wrapper for in-cell loading indicators
    const CustomDateCellWrapper = ({ children, value }) => {
      if (view === Views.MONTH && loadingBatches.size > 0) {
        // Check if this date falls within any loading batch
        const cellDate = moment(value).format("YYYY-MM-DD");
        let isLoading = false;

        // Check if this cell's date falls within any loading batch range
        for (let batchKey of loadingBatches) {
          const batchStart = moment(batchKey);
          const batchEnd = batchStart.clone().add(6, "days");
          if (moment(cellDate).isBetween(batchStart, batchEnd, "day", "[]")) {
            isLoading = true;
            break;
          }
        }

        if (isLoading) {
          return (
            <div style={{ position: "absolute" }}>
              {children}
              <div className="calendar-cell-loading">
                <div className="loading-spinner"></div>
              </div>
            </div>
          );
        }
      }
      return children;
    };

    // Custom time slot wrapper for week/work_week views
    const CustomTimeSlotWrapper = ({ children, value, resource }) => {
      if (
        (view === Views.WEEK || view === Views.WORK_WEEK) &&
        loadingBatches.size > 0
      ) {
        // Check if this time slot's date is loading
        const slotDate = moment(value).format("YYYY-MM-DD");
        const isLoading = loadingBatches.has(slotDate);

        if (isLoading) {
          return (
            <div style={{ position: "relative", height: "100%" }}>
              {children}
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 1000,
                  pointerEvents: "none",
                }}
              >
                <div
                  className="loading-spinner"
                  style={{ width: "16px", height: "16px" }}
                ></div>
              </div>
            </div>
          );
        }
      }
      return children;
    };

    // Add weekend class for day view
    const isWeekendDay =
      view === Views.DAY &&
      (moment(date).day() === 0 || moment(date).day() === 6);
    const containerClass = isWeekendDay ? "weekend-day" : "";

    let aStatus = appointmentsService.getAppointmentStatusMap();
    let sStatus = appointmentsService.getSlotStatusMap();

    const eventProcessStart = Date.now();
    devLog(
      `🔍 [TIMING] ${view} - Processing ${
        displayEvents?.length || 0
      } events at ${eventProcessStart}`,
    );

    displayEvents.forEach((event) => {
      if (event.slotStatus != null) {
        event.slotStatusLabel = `${
          sStatus[event.slotStatus]
        } - ${getParticipantsInfo(event.actors)}`;
      } else if (event.appointmentStatus != null) {
        event.appointmentStatusLabel = aStatus[event.appointmentStatus];
      }
    });

    const eventProcessEnd = Date.now();
    devLog(
      `🔍 [TIMING] ${view} - Event processing took ${
        eventProcessEnd - eventProcessStart
      }ms`,
    );
    devLog(
      `🔍 [TIMING] ${view} - Total render time so far: ${
        eventProcessEnd - renderStart
      }ms`,
    );

    const calendarRenderStart = Date.now();
    devLog(
      `🔍 [TIMING] ${view} - About to render <Calendar> component with ${
        (displayEvents || []).length
      } events at ${calendarRenderStart}`,
    );

    // Use displayEvents directly (no artificial limiting)
    let calendarEvents = displayEvents;

    // If agenda display mode is active, render table view instead of calendar
    // Agenda is purely observational - shows current view's data in table format
    if (this.state.displayMode === "agenda") {
      // Calculate agenda length based on current view's date range
      let agendaLengthDays = 31; // default to month
      if (view === Views.WEEK || view === Views.WORK_WEEK) {
        agendaLengthDays = 7;
      } else if (view === Views.DAY) {
        agendaLengthDays = 1;
      } else if (view === Views.MONTH) {
        const monthStart = moment(date).startOf("month");
        const monthEnd = moment(date).endOf("month");
        agendaLengthDays = monthEnd.diff(monthStart, "days") + 1;
      }

      return (
        <div
          style={{ height: "100%", minHeight: "500px", position: "relative" }}
          className={containerClass}
        >
          {/* Custom Toolbar for agenda - show actual view, not AGENDA */}
          <CustomToolbar
            date={date}
            view={Views.AGENDA} // Show Agenda button as active for UI
            onNavigate={this.onNavigate}
            onView={(newView) => {
              // When switching from agenda display mode to any other view,
              // use onNavigate to properly load data for that view
              this.onNavigate(newView, date || new Date());
            }}
            label={formatCompactDateRange(
              "",
              Views.AGENDA, // For label formatting
              date,
              false,
              false,
              agendaLengthDays,
            )}
          />

          {/* Empty state or Custom Agenda View */}
          {calendarEvents.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "60px 40px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "400px",
              }}
            >
              <i
                className="fa fa-calendar-o"
                style={{
                  fontSize: "64px",
                  color: "#d0d0d0",
                  marginBottom: "20px",
                }}
              />
              <div
                style={{
                  fontSize: "1.2em",
                  color: "#6c757d",
                  fontWeight: "500",
                }}
              >
                <Text content={Messages.no_events} />
              </div>
            </div>
          ) : (
            <CustomAgendaView
              events={calendarEvents}
              date={date}
              length={agendaLengthDays}
              onSelectEvent={this.onSelectEvent}
            />
          )}
        </div>
      );
    }

    return (
      <div
        style={{ height: "100%", minHeight: "500px", position: "relative" }}
        className={containerClass}
      >
        {(() => {
          try {
            return (
              <DndProvider backend={HTML5Backend}>
                <Calendar
                  //commenting line below fixes the navigation issue. I don't know why...
                  // key={`calendar-${displayEvents.length}-${loadingBatches.size}`}
                  {...this.props}
                  events={calendarEvents}
                  onNavigate={this.onNavigate}
                  onView={(view) => {
                    // Always use onNavigate to properly load data for the view
                    this.onNavigate(view, this.state.date || new Date());
                  }}
                  currentView={view}
                  onSelectEvent={this.onSelectEvent} // now forwards native event
                  currentDate={date}
                  scrollToTime={scrollToTime} // stable value, only changes on view change
                  popup={true}
                  popupOffset={30}
                  loading={(() => {
                    const isAgendaLoading =
                      view === Views.AGENDA &&
                      (!calendarEvents || calendarEvents.length === 0);
                    devLog("Calendar loading state:", {
                      view,
                      eventsCount: calendarEvents?.length,
                      isLoading: isAgendaLoading,
                    });
                    return isAgendaLoading;
                  })()}
                  length={view === Views.AGENDA ? 1000 : this.props.length}
                  agendaLength={
                    view === Views.AGENDA ? 1000 : this.props.agendaLength
                  }
                  components={{
                    toolbar: CustomToolbar,
                    dateCellWrapper: CustomDateCellWrapper,
                    timeSlotWrapper: CustomTimeSlotWrapper,
                    ...(this.props.components || {}),
                    event: this.props.components?.event
                      ? this.renderEventWithView
                      : undefined,
                  }}
                />
              </DndProvider>
            );
          } catch (error) {
            console.error("Error rendering Calendar component:", error);
            devLog("Calendar events that caused error:", calendarEvents);
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "400px",
                  flexDirection: "column",
                  color: "#6c757d",
                  textAlign: "center",
                }}
              >
                <h5>Calendar Error</h5>
                <p>There was an issue loading the calendar view.</p>
                <p style={{ fontSize: "0.9em" }}>
                  Events: {calendarEvents.length}, View: {view}
                </p>
                <button
                  onClick={() => {
                    devLog("Forcing re-render...");
                    this.forceUpdate();
                  }}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    marginTop: "10px",
                  }}
                >
                  Try Again
                </button>
              </div>
            );
          }
        })()}

        {/* Loading overlay for Week/Day views */}
        {this.state.isLoadingWeekData && (
          <div className="calendar-loading-overlay">
            <div className="calendar-loading-content">
              <div className="calendar-loading-spinner" />
              <div className="calendar-loading-text">
                Loading {view.toLowerCase()} view...
              </div>
            </div>
          </div>
        )}

        {/* Progressive loading indicator for Week view - only show if we have events to load */}
        {this.state.isProgressiveLoading &&
          this.props.events &&
          this.props.events.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "rgba(32, 168, 216, 0.9)",
                color: "white",
                padding: "8px 12px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "500",
                zIndex: 1001,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  border: "2px solid rgba(255,255,255,0.3)",
                  borderTop: "2px solid white",
                  borderRadius: "50%",
                  animation: "calendar-spinner 0.8s linear infinite",
                }}
              />
              Loading day {this.state.progressiveBatchIndex + 1}... (
              {displayEvents.length}/{this.props.events?.length || 0} events)
            </div>
          )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    appTheme: state.user.appTheme || {},
    currentUser:
      {
        ...state.user.currentUser,
        spaceRole: state.user.role, // Include role for context detection
      } || {},
  };
};

export default connect(mapStateToProps)(AppointmentsSlotsCalendar);
